<template>
  <IconButton
    v-if="isMobile"
    id="spotlight.button"
    name="spotlight.button"
    icon-name="material-symbols:search"
    variant="ghost"
    size="md"
    class="ms-auto"
    @click="dialog?.openModal()"
  />
  <BootstrapButton
    v-else
    id="spotlight.button"
    name="spotlight.button"
    variant="ghost"
    class="border mx-auto"
    style="min-width: 18rem; max-width: 32rem"
    :aria-label="translate('Search or go to...')"
    @click="dialog?.openModal()"
  >
    <Icon icon="material-symbols:search" size="md" />
    <span class="flex-grow-1 small text-truncate text-start ms-1">{{
      translate('Search or go to...')
    }}</span>
    <!-- prettier-ignore -->
    <svg class="iconify iconify--material-symbols ms-3" width="1em" height="1em" aria-hidden="true" data-v-a01d590b="" role="img" size="md" fill="currentColor" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg"><path d="m2.5 10.5c-0.275 0-0.51033-0.0978-0.706-0.2935s-0.29367-0.43117-0.294-0.7065v-7c0-0.275 0.098-0.51033 0.294-0.706s0.43133-0.29367 0.706-0.294h7c0.275 0 0.5105 0.098 0.7065 0.294s0.2938 0.43133 0.2935 0.706v7c0 0.275-0.0978 0.5105-0.2935 0.7065s-0.43117 0.2938-0.7065 0.2935h-7zm0-1h7v-7h-7v7z"/><path d="m7.768 3.5251-4.2426 4.2426 0.70711 0.7071 4.2426-4.2426-0.7071-0.7071z"/></svg>
  </BootstrapButton>
  <DialogDrawer
    id="spotlight.dialog"
    ref="dialog"
    :class="['spotlight-dialog', {'spotlight-dialog-loading': loading}]"
    placement="centre"
    modal
    @close="reset"
  >
    <BootstrapFormControl
      v-model="model"
      name="spotlight"
      placeholder="Type to search..."
      only-input
      autofocus
      class="p-2 border-bottom"
      @input="debouncedFn"
      @enter="search"
    >
      <Icon icon="material-symbols:search" size="md" />
    </BootstrapFormControl>
    <SettingsDialogListGroup
      v-if="list.size > 0"
      :list="list"
      class="overflow-y-scroll h-100 mb-0 py-2 px-2"
      @update:model-value="navigate"
    />
    <section
      v-else
      class="d-flex flex-column justify-content-center align-items-center p-3 w-100 text-secondary"
      style="height: 9999px"
    >
      <ErrorMessageAlert v-if="errorMessage" :error-message="errorMessage" />
      <ul v-else-if="loading" class="placeholder-glow placeholder-list">
        <li v-for="i in placeholderNum(15, 25)" :key="i" :class="['w-100 mb-2', {'mt-3': i !== 1}]">
          <i class="placeholder placeholder-lg ratio ratio-1x1 me-2"></i>
          <span :class="['placeholder placeholder-lg', `col-${placeholderNum(7, 12)}`]"></span>
        </li>
      </ul>
      <template v-else-if="lastSearch === undefined">
        <Icon icon="material-symbols:search" class="fs-1 mb-2" />
        <p class="m-0">{{ translate('Find pages throughout Sphere') }}</p>
      </template>
      <template v-else-if="list.size === 0">
        <h2 class="mb-2">{{ lenny }}</h2>
        <p class="m-0">{{ translate('No results for') }} "{{ lastSearch }}"</p>
      </template>
    </section>
  </DialogDrawer>
</template>
<script lang="ts" setup>
import DialogDrawer from '@/components/shared/custom/DialogDrawer/DialogDrawer.vue';
import {useDebounceFn} from '@vueuse/core';
const dialog = useTemplateRef<InstanceType<typeof DialogDrawer>>('dialog');
const emit = defineEmits<{navigate: [meta: SpherePageMeta<SpherePageType>]}>();
const {placeholderNum} = usePlaceholderNum();
const {isMobile} = useBsBreakpoints();
const {translate} = useLocalisation();

const model = ref('');
const lastSearch = ref(); // Handy for starting off
let results: SpherePage<SpherePageType>[] = [];
const list = reactive(new Map<string | undefined, [string, string][]>());

const loading = ref(false);
const errorMessage = ref<string>();

let requestId = 0; // Guards against stale (slower) responses overwriting newer ones

const search = async () => {
  const query = model.value.trim();
  const currentRequest = ++requestId;
  if (query === '') {
    lastSearch.value = undefined;
    list.clear();
    errorMessage.value = undefined;
    loading.value = false;
    return;
  }
  loading.value = true;
  errorMessage.value = undefined;
  try {
    lastSearch.value = model.value;
    const response = await searchCmsPages(model.value);
    if (currentRequest !== requestId) return;
    const excluded = ['cms.CoreIndexPage', 'cms.CategoryIndexPage'];
    results = response.data.results.filter((r) => excluded.includes(r.meta.type) === false);
    const pages: [string, string][] = [];
    const reports: [string, string][] = [];
    const documents: [string, string][] = [];
    const folders: [string, string][] = [];

    for (const {meta} of results) {
      const icon = meta.icon || 'article';
      switch (meta.type) {
        case 'cms.ReportPage':
          reports.push([meta.title, icon]);
          break;
        case 'cms.DocumentPage':
          documents.push([meta.title, icon]);
          break;
        case 'cms.FolderPage':
          folders.push([meta.title, icon]);
          break;
        default:
          pages.push([meta.title, icon]);
          break;
      }
    }

    list.clear();
    if (pages.length > 0) list.set(translate('Pages'), pages);
    if (reports.length > 0) list.set(translate('Reports'), reports);
    if (documents.length > 0) list.set(translate('Documents'), documents);
    if (folders.length > 0) list.set(translate('Folders'), folders);
  } catch (error: any) {
    if (currentRequest !== requestId) return;
    errorMessage.value = error;
  } finally {
    if (currentRequest === requestId) loading.value = false;
  }
};

const debouncedFn = useDebounceFn(search, 1000, {maxWait: 10000});

const navigate = (title: string) => {
  // N.B. This is a page & not a navigation node.
  const page = results.find((r) => r.meta.title === title);
  if (page) {
    emit('navigate', page.meta);
    dialog.value?.close();
    model.value = '';
  }
};

const getLenny = () => {
  const lenny = ["(='X'=)", '(>_<)', '(o^^o)', '(^_^)', '/(o_o)/', '(˚Δ˚)', '¯\\_(ツ)_/¯'];
  const random = Math.floor(Math.random() * lenny.length);
  return lenny[random];
};
const lenny = ref(getLenny());

const reset = () => {
  requestId++; // Invalidate any in-flight request
  loading.value = false;
  errorMessage.value = undefined;
  model.value = '';
  lastSearch.value = undefined;
  list.clear();
  lenny.value = getLenny();
};

window.addEventListener('keydown', (e: KeyboardEvent) => {
  const activeElement = document.activeElement as HTMLElement;
  const isEditable = activeElement?.matches('input, textarea') || activeElement?.isContentEditable;
  //should cover the native dialogs & bootstrap hopefully but I think those are mostly phased out
  const hasOpenDialog =
    document.querySelector('dialog[open]') || document.querySelector('[aria-modal="true"]');

  if (e.key === '/' && dialog.value?.open === false && !isEditable && !hasOpenDialog) {
    e.preventDefault();
    dialog.value.openModal();
  } else if (e.key === 'Escape' && dialog.value?.open === true) {
    e.preventDefault();
    dialog.value.close();
    reset();
  }
});
</script>
<style lang="scss" scoped>
@use '@/scss/breakpoints/breakpoints' as *;

.dialog {
  margin-top: 6rem;
  max-height: 70%;
  max-width: 48rem;
  border-radius: 0.75rem;

  :deep(.card-body) {
    display: flex;
    flex-direction: column;
    padding: 0;
  }

  @include media-breakpoint-down(md, $grid-breakpoints) {
    $margin: calc(var(--shell-header-height) * 1.5);

    margin-top: $margin;
    max-height: calc(100vh - $margin);
    width: 100vw;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}

.spotlight-dialog-loading {
  :deep(form) {
    overflow-y: hidden !important;
  }
}

.placeholder-list {
  width: 100%;
  height: 100%;
  padding: 0%;
  margin: 0;
  overflow-y: hidden;
  list-style: none;

  i {
    width: 1.2rem;
  }
}

:deep(.btn-close) {
  display: none;
}
</style>
